/**
 * INCSUBPoolCard.jsx
 *
 * A visual card component for displaying brief information about a pool.
 * Includes interaction logic to navigate to pool details, updates the Redux store
 * with path and state metadata, and shows host, type, status, and member visuals.
 *
 * @component
 * @author Vishal Lohbande
 */

import React from "react";
import './PoolCard.css';
import { useDispatch, useSelector } from "react-redux";
import { setPlatformPath } from "../../redux/commonSlice";
import { setPlatformPathRoot } from "../../features/platforms/platformsSlice";
import {  setPoolDetails} from "../../features/pools/poolsSlice";
/**
 * INCSUBPoolCard Component
 *
 * @param {Object} props - React props
 * @param {Object} props.pool - Pool object containing pool metadata
 * @param {Function} props.func - Callback function executed on card click (e.g., to set selected pool)
 *
 * @returns {JSX.Element} Rendered PoolCard
 */
function INCSUBPoolCard({ pool, func }) {
    const dispatch = useDispatch();

    // Redux state: root path used for breadcrumb-like platform pathing
    const platformPathRoot = useSelector((state) => state.platforms.platformPathRoot);

    // Redux state: current routing status
    const routeStatus = useSelector((state) => state.common.routeStatus);

    /**
     * Handles the click on the card to:
     * - Set the platform path to pool details
     * - Trigger provided callback
     * - Set pools page flag to false
     * - Append pool name to root path if currently on /platforms
     *
     * @function handleCardClick
     * @param ()
     * @author Vishal Lohbande
     */
    const handleCardClick = () => {
        dispatch(setPoolDetails(null))
        dispatch(setPlatformPath("/platform/plan/pools/poolsDetails"));
        func(pool);

        if (routeStatus === '/platforms') {
            dispatch(setPlatformPathRoot(platformPathRoot + ' / ' + pool?.poolName));
        }
    };

    return (
        <div className="INCSUBPoolCard_ctn" onClick={handleCardClick}>
            <div className="INCSUBPoolCard_info_ctn">
                {/* Pool Type and Status */}
                <div className="INCSUBPoolCard_info_ctn__type_btn">
                    <div className="INCSUBPoolCard_info_ctn__type_text">{pool?.poolType}</div>
                    <div className={`INCSUBPoolCard_info_ctn__textStatus ${pool?.poolState}`}>
                        <div className={`INCSUBPoolCard_info_ctn__textStatus_dot ${pool?.poolState}`}></div>
                        <div className={`INCSUBPoolCard_status ${pool?.poolState}`}>{pool?.poolState}</div>
                    </div>
                </div>

                {/* Pool Name */}
                <div
                    className="INCSUBPoolCard_info_ctn_poolTitle"
                    title={pool?.poolName}
                >
                    {pool?.poolName?.length > 27
                        ? `${pool.poolName.slice(0, 27)}...`
                        : pool.poolName}
                </div>

                {/* Pool Start Date */}
                <div className="INCSUBPoolCard_info_ctn_poolDate">
                    {pool?.poolStartDate || "Yet to be Started"}
                </div>

                {/* Host Information */}
                <div className="INCSUBPoolCard_info_ctn_poolhost_info">
                    <div className="INCSUBPoolCard_info_ctn_poolhost_info_dot"></div>
                    <div className="INCSUBPoolCard_info_ctn_poolhost_title">Host :</div>
                    <div className="INCSUBPoolCard_info_ctn_poolhost_name">{pool?.hostName}</div>
                </div>
            </div>

            <div className="INCSUBPoolCard_info_ctn_poolhr"></div>

            {/* Member Images + Count */}
            <div className="INCSUBPoolCard_info_ctn_members">
                {
                    pool.memberImages?.length > 0 &&
                    pool.memberImages.map((image, index) => (
                        <div
                            key={index}
                            className="INCSUBPoolCard_info_ctn_members_img"
                            style={{
                                position: 'absolute',
                                left: `${index * 25}px`,
                                zIndex: 5 - index
                            }}
                        >
                            <img src={image? image:'/assets/icons/common/defaultImage.png'} alt='pool member' />
                        </div>
                    ))
                }

                <div
                    style={{
                        position: 'absolute',
                        left: `${pool.memberImages ? (pool.memberImages.length - 1) * 28 : 0}px`
                    }}
                    className="INCSUBPoolCard_info_ctn_membercount"
                >
                    {pool?.currentMembers}/{pool?.memberAllowed} members
                </div>
            </div>
        </div>
    );
}

export default INCSUBPoolCard;
